import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Checkbox } from "@/components/ui/checkbox";
import { useApiFetch } from "@/helper/apiFetch";
import { SurveySchema } from "./PlenumSchema";

type SurveyInfoProps = {
  Id: number | null;
  plenumId: number;
  eventId: string;
  onClose: (refresh: boolean) => void;
};

export function SurveyInfo({ Id, plenumId, eventId, onClose }: SurveyInfoProps) {
  const apiFetch = useApiFetch();
  const [isLoaded, setIsLoaded] = useState(false);
  const [errorText, setError] = useState("");
  const creatorElementRef = useRef<HTMLDivElement>(null);
  const creatorRef = useRef<any>(null);
    let creator: any = null;
  const form = useForm<z.infer<typeof SurveySchema>>({
    resolver: zodResolver(SurveySchema),
    defaultValues: {
      PlenumId: plenumId,
      EventId: eventId,
      Data: "{}",
      Name: "",
      IsAnonymous: false,
      IsRedlofSurvey: true,
      Live: false,
      ResultViewOn: false,
    },
  });

  const onSubmit = async (values: z.infer<typeof SurveySchema>) => {
    await Save(values, true);
  }
     const Save = async (values: z.infer<typeof SurveySchema>, close: boolean): Promise<number | null> => {
    try {
      setError("");

      const newData = creatorRef.current?.SurveyText?.() ?? values.Data ?? "{}";
      const sendValues = { ...values, Data: newData };
      form.setValue("Data", newData);

      const res = await apiFetch(
        `/admin/SaveSurveyDesignNew`,
        {
          method: "POST",
          body: JSON.stringify(sendValues),
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!res.ok) {
        const e = await res.text();
        setError(e);
        return null;
      }
        if(close)
          onClose(true);
        else
        {
            var newId = await res.json();
            form.setValue("Id", newId.Id);
            creator.setPreviewParameters(eventId, newId.Id);
            return newId.Id;

        }    
    } catch (err) {
      console.error(err);
      setError("Failed to save survey");
      return null;
    }
    return null;
  };

  const runSurveyDesigner = () => {
    const SurveyCreatorCtor = (window as any).SurveyCreatorRedlof;
    if (!SurveyCreatorCtor) {
      setError("Survey designer script is not available");
      return;
    }

     creator = new SurveyCreatorCtor(null);
    creatorRef.current = creator;

    const surveyData = form.getValues("Data") ?? "{}";
    try {
      creator.Survey(JSON.parse(surveyData));
    } catch {
      creator.Survey({});
    }

    creator.setPreviewParameters(eventId, form.getValues("Id") ?? 0);

    creator.SaveSurveyFunc = function () {
      void Save(form.getValues(), false);
    };

    if (creatorElementRef.current) {
      const jquery = (window as any).jQuery ?? (window as any).$;
      if (jquery) {
        const renderTarget = jquery(creatorElementRef.current);
        renderTarget.empty();
        creator.Render(renderTarget);
      } else {
        creatorElementRef.current.innerHTML = "";
        creator.Render(creatorElementRef.current);
      }
    }
  };

  const loadExternalScript = (src: string) => {
    return new Promise<void>((resolve, reject) => {
      const existing = document.querySelector(`script[data-survey-src=\"${src}\"]`) as HTMLScriptElement | null;
      if (existing) {
        if (existing.dataset.loaded === "true") {
          resolve();
          return;
        }
        existing.addEventListener("load", () => resolve(), { once: true });
        existing.addEventListener("error", () => reject(new Error(`Failed to load script: ${src}`)), { once: true });
        return;
      }

      const script = document.createElement("script");
      script.src = src;
      script.async = true;
      script.dataset.surveySrc = src;
      script.onload = () => {
        script.dataset.loaded = "true";
        resolve();
      };
      script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
      document.head.appendChild(script);
    });
  };

  const loadExternalCss = (href: string) => {
    return new Promise<void>((resolve) => {
      const existing = document.querySelector(`link[data-survey-css=\"${href}\"]`) as HTMLLinkElement | null;
      if (existing) {
        resolve();
        return;
      }

      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = href;
      link.dataset.surveyCss = href;
      link.onload = () => resolve();
      link.onerror = () => resolve();
      document.head.appendChild(link);
    });
  };

  useEffect(() => {
    if ((Id ?? 0) > 0) {
      apiFetch(`/Admin/GetSurvey?id=${Id}`)
        .then((res) => {
          if (!res.ok) throw new Error("Failed to fetch survey");
          return res.json();
        })
        .then((data) => {
          form.reset(data);
          setIsLoaded(true);
        })
        .catch((err) => {
          console.error(err);
          setError("Failed to load survey");
          setIsLoaded(true);
        });
    } else {
      setIsLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (!isLoaded) return;

    let active = true;
    const initDesigner = async () => {
      try {
        await loadExternalCss("https://one.beamstream.eu/css/surveyRedlof.css");
        await loadExternalScript("https://one.beamstream.eu/lib/jquery/jquery.min.js");
        await loadExternalScript("https://one.beamstream.eu/js/survey/surveyRedlof.viewer.js");
        await loadExternalScript("https://one.beamstream.eu/js/survey/surveyRedlof.designer.js");
        if (!active) return;
        runSurveyDesigner();
      } catch (err) {
        console.error(err);
        setError("Failed to load survey designer assets");
      }
    };

    void initDesigner();

    return () => {
      active = false;
    };
  }, [isLoaded]);

  return (
    <div className="p-5">
      {!isLoaded ? (
        <div>Loading...</div>
      ) : (
        <div>
          <Form {...form}>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                void form.handleSubmit(onSubmit, (error) => {
                  console.log("Validation errors:", error, form.getValues());
                })(e);
              }}
              className="space-y-2"
            >
              <div className="flex gap-3 justify-start items-center">
                <FormField
                  control={form.control}
                  name="Name"
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormLabel>Survey Name</FormLabel>
                      <FormControl>
                        <Input {...field} value={field.value ?? ""} />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="IsAnonymous"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Checkbox
                          label="Set EVERYONE as anonymous"
                          checked={!!field.value}
                          onCheckedChange={field.onChange}
                          name={field.name}
                          onBlur={field.onBlur}
                          ref={field.ref}
                          className="mt-3"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              <div className="text-lg font-semibold border-b border-slay-500">
                <div id="errorElement"></div>
                <div ref={creatorElementRef} id="creatorElement"></div>
              </div>

              <div className="grid-default">
                <div></div>
                <div className="grid-default">
                  <Button className="w-full" type="button" onClick={() => onClose(false)}>
                    Cancel
                  </Button>

                  <Button className="w-full" type="submit">
                    Save
                  </Button>
                </div>
              </div>

              {errorText !== "" && <div className="text-red-500 mt-2">{errorText}</div>}
            </form>
          </Form>
        </div>
      )}
    </div>
  );
}
